import React, { useMemo } from 'react';
import { PolicyTemplate, NestedCondition, AINEDetails, DINEDetails, ModifyDetails, PolicyRuleIf, SimpleCondition, CountCondition, NotCondition, Condition } from '../../../types/policy';
import { GraphView } from '../../PolicyVisualizer/GraphView/GraphView';
import { Tooltip } from '../../common/Tooltip';

/**
 * Type guard to check if details are AINEDetails
 */
const isAINEDetails = (details: ModifyDetails | AINEDetails | DINEDetails | undefined): details is AINEDetails => {
  return details !== undefined && 'type' in details && !('deployment' in details);
};

/**
 * Type guard to check if details are DINEDetails
 */
const isDINEDetails = (details: ModifyDetails | AINEDetails | DINEDetails | undefined): details is DINEDetails => {
  return details !== undefined && 'deployment' in details;
};

/**
 * Converts PolicyRuleIf to the appropriate condition type for existence condition
 */
const policyRuleIfToExistenceCondition = (policyRuleIf: PolicyRuleIf): Condition | undefined => {
  if (!policyRuleIf || Object.keys(policyRuleIf).length === 0) {
    return undefined;
  }
  
  // Direct SimpleCondition (check for either 'field' or 'value')
  if ('field' in policyRuleIf || 'value' in policyRuleIf) {
    return policyRuleIf as SimpleCondition;
  }
  
  // Direct CountCondition
  if ('count' in policyRuleIf) {
    return policyRuleIf as CountCondition;
  }
  
  // NestedCondition with allOf/anyOf
  if (policyRuleIf.allOf || policyRuleIf.anyOf) {
    const result: NestedCondition = {};
    
    if (policyRuleIf.allOf) {
      result.allOf = policyRuleIf.allOf as Condition[];
    }
    
    if (policyRuleIf.anyOf) {
      result.anyOf = policyRuleIf.anyOf as Condition[];
    }
    
    return result;
  }
  
  // NotCondition
  if (policyRuleIf.not) {
    return { not: policyRuleIf.not } as NotCondition;
  }
  
  return undefined;
};

/**
 * Converts existence condition back to PolicyRuleIf for GraphView
 */
const existenceConditionToPolicyRuleIf = (condition: Condition | undefined): PolicyRuleIf => {
  if (!condition) {
    return {};
  }
  
  // If it's a NotCondition, return it with proper structure
  if ('not' in condition) {
    return { not: condition.not } as PolicyRuleIf;
  }
  
  // If it's a SimpleCondition or CountCondition, return it directly
  if ('field' in condition || 'value' in condition || 'count' in condition) {
    return condition as PolicyRuleIf;
  }
  
  // If it's a NestedCondition, return it directly
  return condition as PolicyRuleIf;
};

/**
 * Props interface for the ExistenceConditionTab component
 * @property policy - The current policy template being edited
 * @property onUpdatePolicy - Callback for updating the entire policy object
 */
interface ExistenceConditionTabProps {
  policy: PolicyTemplate;
  onUpdatePolicy: (updatedPolicy: PolicyTemplate) => void;
}

/**
 * ExistenceConditionTab component - Manages existence condition configuration for AINE and DINE effects
 * 
 * This component provides a GraphView interface for building existence conditions
 * used with the "auditIfNotExists" and "deployIfNotExists" policy effects. It creates a virtual policy
 * containing only the existence condition for GraphView compatibility.
 */
export const ExistenceConditionTab: React.FC<ExistenceConditionTabProps> = ({
  policy,
  onUpdatePolicy
}) => {
  // Get the current existence condition from policy details
  const getCurrentExistenceCondition = (): Condition | undefined => {
    const details = policy.properties.policyRule.then.details;
    
    if (isAINEDetails(details) && details.existenceCondition) {
      return details.existenceCondition;
    }
    
    if (isDINEDetails(details) && details.existenceCondition) {
      return details.existenceCondition;
    }
    
    return undefined;
  };

  // Create virtual policy for GraphView
  const virtualPolicy = useMemo(() => {
    const existenceCondition = getCurrentExistenceCondition();
    const virtualIf = existenceConditionToPolicyRuleIf(existenceCondition);
    
    return {
      properties: {
        displayName: policy.properties.displayName || '',
        description: policy.properties.description || '',
        policyType: policy.properties.policyType || 'Custom',
        mode: policy.properties.mode || 'All',
        metadata: {
          version: policy.properties.metadata?.version || '1.0.0',
          category: policy.properties.metadata?.category || '',
          preview: policy.properties.metadata?.preview || false
        },
        version: policy.properties.version || '1.0.0',
        parameters: { ...policy.properties.parameters },
        policyRule: {
          if: virtualIf,
          then: {
            effect: policy.properties.policyRule.then.effect || 'audit',
            ...(policy.properties.policyRule.then.details && { details: policy.properties.policyRule.then.details })
          }
        }
      }
    };
  }, [policy]);

  // Handle updates from GraphView
  const handleVirtualPolicyUpdate = (updatedVirtualPolicy: PolicyTemplate) => {
    const newExistenceCondition = policyRuleIfToExistenceCondition(updatedVirtualPolicy.properties.policyRule.if);
    
    // Deep clone policy for updates
    const updatedPolicy: PolicyTemplate = {
      ...policy,
      properties: {
        ...policy.properties,
        policyRule: {
          ...policy.properties.policyRule,
          then: {
            ...policy.properties.policyRule.then
          }
        }
      }
    };
    
    const details = updatedPolicy.properties.policyRule.then.details;
    
    if (isAINEDetails(details)) {
      updatedPolicy.properties.policyRule.then.details = {
        ...details,
        existenceCondition: newExistenceCondition || {}
      };
    } else if (isDINEDetails(details)) {
      updatedPolicy.properties.policyRule.then.details = {
        ...details,
        existenceCondition: newExistenceCondition || {}
      };
    } else {
      updatedPolicy.properties.policyRule.then.details = {
        type: '',
        existenceCondition: newExistenceCondition || {}
      };
    }
    
    onUpdatePolicy(updatedPolicy);
  };

  return (
    <div className="flex flex-col space-y-6 w-full min-w-0 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm">
      <div className="space-y-4 w-full">
        {/* Header with description */}
        <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Existence Condition
            </h3>
            <Tooltip 
              content="Define the condition that must be met on related resources for the policy to be satisfied. This condition will be evaluated against resources of the type specified in the Details tab. If no matching resources are found that satisfy this condition, the policy will trigger an audit result (for AINE) or deploy resources (for DINE). Note: This condition is separate from your main policy condition - it determines what related resources should exist."
              position="right"
              maxWidth={400}
            />
          </div>
        </div>

        {/* GraphView for building existence conditions */}
        <div className="flex-grow">
          <GraphView
            policy={virtualPolicy}
            onUpdate={handleVirtualPolicyUpdate}
          />
        </div>
      </div>
    </div>
  );
}; 